/**
 * Pure function for confirm step summary computation.
 *
 * Extracted from the orchestrator because it has non-trivial merge logic
 * (server/client counts, errorCode partitioning, fallback paths) that
 * benefits from isolated property-based testing.
 *
 * The view getter itself (`_confirmView`) stays inline in the orchestrator
 * as a simple precedence waterfall — same pattern as `_setupView` and
 * `_previewUploadView`.
 */

import type { ReviewValidationSummary } from '../csv-import-validation-summary/csv-import-validation-summary';
import type { BatchValidationResult } from '../../validators/row-validator';
import type { ImportSubmitError } from '../../types/template';

// ============================================================================
// Types
// ============================================================================

/** Re-export for convenience — consumers can import from either location. */
export type ServerErrorEntry = ImportSubmitError;

// ============================================================================
// computeConfirmSummary
// ============================================================================

/**
 * Computes the confirm step summary by merging server and client counts.
 *
 * @param clientResult - Client-side validation result (may be null)
 * @param serverSummary - Server-reported summary with created count (may be undefined)
 * @param serverErrors - Server-reported errors array (may be undefined)
 */
export function computeConfirmSummary(
  clientResult: Pick<
    BatchValidationResult,
    'validCount' | 'invalidCount' | 'duplicateCount'
  > | null,
  serverSummary: { created: number } | undefined,
  serverErrors: ServerErrorEntry[] | undefined,
): ReviewValidationSummary {
  // Defensive: if both sources are null/absent, default all counts to 0
  if (!clientResult && !serverSummary) {
    return { totalRows: 0, validCount: 0, invalidCount: 0, duplicateCount: 0 };
  }

  const errors = serverErrors ?? [];

  const validCount = serverSummary
    ? serverSummary.created
    : (clientResult?.validCount ?? 0);

  const invalidCount =
    (clientResult?.invalidCount ?? 0) +
    errors.filter((e) => e.errorCode !== 'duplicate').length;

  const duplicateCount =
    (clientResult?.duplicateCount ?? 0) +
    errors.filter((e) => e.errorCode === 'duplicate').length;

  const totalRows = validCount + invalidCount + duplicateCount;

  return { totalRows, validCount, invalidCount, duplicateCount };
}
